/*
 * Behavior: the browser environment / fingerprint looks like a bot. Runs once on
 * mount and reports 'fingerprint' if the user agent matches a known bot keyword,
 * or if an environment value is one a real browser would never produce.
 */
import { useEffect } from 'react'
import { getIsBotTraffic } from 'lib/bots/helpers'
import { TrackBotTrafficAction, userAgentBotEvent } from 'lib/bots/trackBotTraffic'

const useUserAgentBotCheck = (report: (action: TrackBotTrafficAction) => void): void => {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const matchesBotUserAgent = getIsBotTraffic(navigator.userAgent)

    // The browser is being driven by automation. navigator.webdriver is set by
    // Selenium / Puppeteer / Playwright; the globals are markers left by
    // PhantomJS (_phantom / callPhantom) and Nightmare.js (__nightmare). Real
    // users never have any of these.
    const isAutomated =
      navigator.webdriver === true ||
      '_phantom' in window ||
      'callPhantom' in window ||
      '__nightmare' in window

    // Real browsers always report at least one preferred language. Only an
    // explicit empty list counts; an older browser missing the property is left
    // alone (not treated as empty).
    const hasNoLanguages = Array.isArray(navigator.languages) && navigator.languages.length === 0

    // Real devices report at least one CPU core. Only an explicit 0 counts;
    // undefined on very old browsers is left alone.
    const hasNoCpuCores = navigator.hardwareConcurrency === 0

    // A real screen has a non-zero size; 0x0 is typical of headless browsers.
    const hasEmptyScreen =
      !!window.screen && (window.screen.width === 0 || window.screen.height === 0)

    // Empty plugins is normal on mobile and on Firefox/Brave, so this is only a
    // signal for engines that always expose the standard PDF-viewer plugins:
    // Chromium (Chrome/Edge) and desktop Safari. Firefox is not matched, and
    // Brave (Chromium UA, but its shields change plugins) is excluded. Unknown
    // browsers are left alone so we never guess.
    const userAgent = navigator.userAgent
    const isChromium = /Chrome\//.test(userAgent) // Chrome, Edge, and other Chromium
    const isSafari = /Safari\//.test(userAgent) && !/Chrome\//.test(userAgent)
    const isBrave = 'brave' in navigator
    const alwaysReportsPlugins = (isChromium || isSafari) && !isBrave

    // Real mobile Chrome/Safari report zero plugins, so restrict to non-touch
    // desktop. Read the count via ArrayLike to avoid the deprecated .length.
    const isNonTouchDesktop = navigator.maxTouchPoints === 0
    const pluginCount = navigator.plugins ? (navigator.plugins as ArrayLike<unknown>).length : 0
    const hasNoPlugins = isNonTouchDesktop && alwaysReportsPlugins && pluginCount === 0

    // A real desktop Chromium browser (Chrome/Edge/Brave/Opera) always exposes
    // window.chrome; its absence on desktop points to headless automation. Mobile
    // is excluded because Android in-app / WebView browsers use a Chrome UA
    // without window.chrome and are real users.
    const hasNoChromeObject = isNonTouchDesktop && isChromium && !window.chrome

    // remove once we change for the real event
    if (matchesBotUserAgent) {
      userAgentBotEvent()
    }

    if (
      // matchesBotUserAgent || // uncomment once we change for the real event
      isAutomated ||
      hasNoLanguages ||
      hasNoCpuCores ||
      hasEmptyScreen ||
      hasNoPlugins ||
      hasNoChromeObject
    ) {
      report('fingerprint')
    }
  }, [])
}

export default useUserAgentBotCheck
